import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { 
  AlertOctagon, 
  AlertTriangle, 
  CheckCircle2, 
  Info, 
  X, 
  ChevronDown, 
  ChevronUp, 
  Copy, 
  Check,
  Trash2
} from 'lucide-react';

export type ToastType = 'error' | 'warning' | 'success' | 'info';

export interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  details?: string[] | string;
  duration?: number; // duration in ms, 0 = sticky
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: number;
}

export interface ToastOptions {
  message?: string;
  details?: string[] | string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (type: ToastType, title: string, options?: ToastOptions) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
  toast: {
    error: (title: string, message?: string, options?: ToastOptions) => string;
    warning: (title: string, message?: string, options?: ToastOptions) => string;
    success: (title: string, message?: string, options?: ToastOptions) => string;
    info: (title: string, message?: string, options?: ToastOptions) => string;
  };
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const addToast = useCallback((type: ToastType, title: string, options?: ToastOptions): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    
    // Default durations: 7000ms for errors/warnings, 4500ms for success/info
    const defaultDuration = (type === 'error' || type === 'warning') ? 7000 : 4500;
    const duration = options?.duration !== undefined ? options.duration : defaultDuration;

    const newToast: ToastItem = {
      id,
      type,
      title,
      message: options?.message,
      details: options?.details,
      duration,
      action: options?.action,
      createdAt: Date.now()
    };

    setToasts((prev) => {
      // Keep at most 5 toasts visible at once
      const updated = [newToast, ...prev];
      if (updated.length > 5) {
        return updated.slice(0, 5);
      }
      return updated;
    });

    return id;
  }, []);

  const toastMethods = {
    error: (title: string, message?: string, options?: ToastOptions) => 
      addToast('error', title, { ...options, message: message ?? options?.message }),
    warning: (title: string, message?: string, options?: ToastOptions) => 
      addToast('warning', title, { ...options, message: message ?? options?.message }),
    success: (title: string, message?: string, options?: ToastOptions) => 
      addToast('success', title, { ...options, message: message ?? options?.message }),
    info: (title: string, message?: string, options?: ToastOptions) => 
      addToast('info', title, { ...options, message: message ?? options?.message })
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearAllToasts, toast: toastMethods }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} onClearAll={clearAllToasts} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// -------------------------------------------------------------
// Toast Card Component
// -------------------------------------------------------------

const ToastCard: React.FC<{
  toast: ToastItem;
  onDismiss: (id: string) => void;
}> = ({ toast, onDismiss }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  React.useEffect(() => {
    if (!toast.duration || toast.duration <= 0 || isPaused) return;

    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, toast.duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, isPaused, onDismiss]);

  const handleCopyDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    const textToCopy = Array.isArray(toast.details) 
      ? toast.details.join('\n') 
      : String(toast.details || toast.message || toast.title);
    
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStyleProps = () => {
    switch (toast.type) {
      case 'error':
        return {
          icon: AlertOctagon,
          bgColor: 'bg-zinc-900',
          borderColor: 'border-red-300',
          accentColor: 'text-red-600',
          bgBadge: 'bg-red-50',
          progressBarColor: 'bg-red-500',
          titleColor: 'text-zinc-900',
          badgeText: 'Error'
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-zinc-900',
          borderColor: 'border-amber-300',
          accentColor: 'text-amber-600',
          bgBadge: 'bg-amber-50',
          progressBarColor: 'bg-amber-500',
          titleColor: 'text-zinc-900',
          badgeText: 'Warning'
        };
      case 'success':
        return {
          icon: CheckCircle2,
          bgColor: 'bg-zinc-900',
          borderColor: 'border-emerald-300',
          accentColor: 'text-blue-600',
          bgBadge: 'bg-emerald-50',
          progressBarColor: 'bg-[#1473E6]',
          titleColor: 'text-zinc-900',
          badgeText: 'Success'
        };
      case 'info':
      default:
        return {
          icon: Info,
          bgColor: 'bg-zinc-900',
          borderColor: 'border-orange-300',
          accentColor: 'text-blue-600',
          bgBadge: 'bg-blue-50',
          progressBarColor: 'bg-[#1473E6]',
          titleColor: 'text-zinc-900',
          badgeText: 'Notice'
        };
    }
  };

  const styles = getStyleProps();
  const IconComponent = styles.icon;

  const hasDetails = Boolean(
    toast.details && 
    (Array.isArray(toast.details) ? toast.details.length > 0 : String(toast.details).trim() !== '')
  );

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 450, damping: 30 }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={`relative w-full max-w-md bg-zinc-900 border ${styles.borderColor} rounded-md shadow-xl overflow-hidden pointer-events-auto select-none`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon Badge */}
          <div className={`w-9 h-9 rounded-md ${styles.bgBadge} ${styles.accentColor} flex items-center justify-center shrink-0 shadow-2xs`}>
            <IconComponent className="w-5 h-5" />
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0 pr-1">
            <div className="flex items-center gap-2">
              <h5 className={`text-sm font-bold ${styles.titleColor} leading-tight`}>
                {toast.title}
              </h5>
              <span className={`px-1.5 py-0.2 rounded text-[10px] font-bold uppercase tracking-wider ${styles.bgBadge} ${styles.accentColor}`}>
                {styles.badgeText}
              </span>
            </div>

            {toast.message && (
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed break-words">
                {toast.message}
              </p>
            )}

            {/* Expandable Details Section */}
            {hasDetails && (
              <div className="mt-2.5">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-1 text-[11px] font-bold text-zinc-500 hover:text-zinc-200 transition-colors cursor-pointer"
                >
                  {showDetails ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5" />
                      Hide Technical Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5" />
                      {Array.isArray(toast.details) 
                        ? `View Details (${toast.details.length} ${toast.details.length === 1 ? 'item' : 'items'})`
                        : 'View Syntax Diagnostics'
                      }
                    </>
                  )}
                </button>

                {showDetails && (
                  <div className="mt-2 p-2.5 bg-zinc-900 text-zinc-100 rounded-md text-[11px] font-mono leading-normal max-h-40 overflow-y-auto border border-zinc-800 animate-in fade-in duration-150">
                    <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-zinc-800 text-zinc-400 text-[10px]">
                      <span>Diagnostic Log</span>
                      <button
                        onClick={handleCopyDetails}
                        className="flex items-center gap-1 hover:text-white transition-colors cursor-pointer"
                        title="Copy details to clipboard"
                      >
                        {copied ? (
                          <>
                            <Check className="w-3 h-3 text-emerald-400" />
                            <span className="text-emerald-400">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>

                    {Array.isArray(toast.details) ? (
                      <ul className="space-y-1 list-disc list-inside">
                        {toast.details.map((detail, idx) => (
                          <li key={idx} className="break-words text-zinc-200">
                            {detail}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <pre className="whitespace-pre-wrap break-words text-zinc-200">
                        {toast.details}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Custom Action Button */}
            {toast.action && (
              <div className="mt-3">
                <button
                  onClick={() => {
                    toast.action?.onClick();
                    onDismiss(toast.id);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold text-white ${styles.progressBarColor} hover:opacity-90 transition-opacity cursor-pointer shadow-2xs`}
                >
                  {toast.action.label}
                </button>
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={() => onDismiss(toast.id)}
            className="text-zinc-400 hover:text-zinc-300 p-1 rounded-md hover:bg-zinc-800 transition-colors shrink-0 cursor-pointer"
            aria-label="Dismiss toast"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Auto-Dismiss Progress Bar */}
      {toast.duration && toast.duration > 0 && !isPaused && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800 overflow-hidden">
          <motion.div
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{ duration: toast.duration / 1000, ease: 'linear' }}
            className={`h-full ${styles.progressBarColor}`}
          />
        </div>
      )}
    </motion.div>
  );
};

// -------------------------------------------------------------
// Toast Container
// -------------------------------------------------------------

const ToastContainer: React.FC<{
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
  onClearAll: () => void;
}> = ({ toasts, onDismiss, onClearAll }) => {
  return (
    <div 
      className="fixed top-4 right-4 z-50 flex flex-col items-end gap-2.5 max-w-md w-full pointer-events-none p-2 sm:p-0"
      style={{ maxHeight: '90vh' }}
    >
      {toasts.length > 2 && (
        <div className="pointer-events-auto">
          <button
            onClick={onClearAll}
            className="flex items-center gap-1.5 px-3 py-1 bg-zinc-900/80 backdrop-blur-xs text-white text-[11px] font-semibold rounded-full hover:bg-zinc-900 transition-colors shadow-md cursor-pointer mb-1"
          >
            <Trash2 className="w-3 h-3 text-zinc-300" />
            Clear all ({toasts.length})
          </button>
        </div>
      )}

      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
};
